import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';

// Refresh 1 minute before the 15-minute access token expires
const REFRESH_INTERVAL = 14 * 60 * 1000;

async function silentRefresh() {
  const res = await axios.post('/api/auth/refresh', {}, { withCredentials: true });
  const { user } = useAuthStore.getState();
  if (user) {
    useAuthStore.getState().setAuth(user, res.data.accessToken);
  }
}

/**
 * Handles automatic token refresh so the user is never logged out mid-session.
 *
 * - On app startup: immediately refreshes the access token if the user is authenticated.
 *   Clears auth only if the refresh token itself is truly expired/invalid.
 * - During active use: proactively refreshes every 14 minutes so the 15-minute
 *   access token never expires while the user is using the app.
 *
 * Returns `isInitialized` — false only during the initial startup refresh check,
 * preventing protected routes from rendering with an expired token.
 */
export function useAuthInitializer() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [isInitialized, setIsInitialized] = useState(false);

  // Startup: validate session by refreshing once on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setIsInitialized(true);
      return;
    }

    silentRefresh()
      .catch(() => {
        // Refresh token is expired or invalid — end the session cleanly
        useAuthStore.getState().clearAuth();
      })
      .finally(() => setIsInitialized(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the session alive with a proactive refresh timer
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      silentRefresh().catch(() => {
        // Proactive refresh failed (network issue or expired token).
        // Don't log out immediately — the response interceptor will handle
        // the next 401 and can retry once before logging out.
      });
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return { isInitialized };
}
